import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "hooks/useStore";
import { useAcademicYearStore } from "store/useAcademicYearStore";
import { getStudentsByParentId, getParentFeesData, getParentAttendanceData, getHomeworkList } from 'helpers/backend_helper';
import { Loader } from "Common/Loader";
import Select from "react-select";
import { Users, Calendar, DollarSign, AlertCircle, CheckCircle, Clock, UserCheck, UserX, BookOpen } from 'lucide-react';
import { Common } from "helpers/common";

const ParentDashboard = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { academicYears, selectedAcademicYear, setSelectedAcademicYear, fetchAcademicYears } = useAcademicYearStore();
    const uiHelper = new Common();
    const baseDocumentUrl = process.env.REACT_APP_DOCUMENT_URL;
    const clientId = 1;

    // State management
    const [isLoading, setIsLoading] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isFetchingData, setIsFetchingData] = useState(false);
    const [students, setStudents] = useState<any[]>([]);
    const [selectedStudent, setSelectedStudent] = useState<any>(null);
    const [feesData, setFeesData] = useState<any[]>([]);
    const [attendanceData, setAttendanceData] = useState<any[]>([]);
    const [homeworkData, setHomeworkData] = useState<any[]>([]);
    const [activeTab, setActiveTab] = useState<'overview' | 'fees' | 'attendance' | 'homework'>('overview');

    // Academic year options
    const academicYearOptions = useMemo(() => {
        const options = academicYears.map((year: any) => ({
            value: year.academicYearId,
            label: year.academicYearName
        }));
        return options.length > 0 ? options : [{ value: '', label: 'No Academic Years Available' }];
    }, [academicYears]);

    // Student options
    const studentOptions = useMemo(() => {
        const options = students.map((student: any) => ({
            value: student.studentId,
            label: `${student.firstName} ${student.lastName}`,
            student: student
        }));
        return options.length > 0 ? options : [{ value: '', label: 'No Students Found' }];
    }, [students]);

    useEffect(() => {
        console.log('ParentDashboard - User state:', user, 'isInitialized:', isInitialized);
        
        // Only run once when user data becomes available
        if (!isInitialized && user) {
            setIsInitialized(true);
            if (user.roleId === 3) {
                console.log('Loading parent data for user:', user.userId);
                loadParentData();
            } else {
                console.log('Redirecting non-parent user to dashboard');
                navigate('/dashboard');
                setIsLoading(false);
            }
        } else if (!user && !isInitialized) {
            // Give a small timeout for user to load from localStorage
            const timer = setTimeout(() => {
                if (!user) {
                    console.log('No user found, redirecting to login');
                    setIsLoading(false);
                    navigate('/login');
                }
            }, 100);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, isInitialized]);

    useEffect(() => {
        if (selectedStudent && selectedAcademicYear) {
            loadFeesAndAttendance();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStudent?.studentId, selectedAcademicYear?.academicYearId]);

    const loadParentData = async () => {
        console.log('loadParentData started');
        setIsLoading(true);
        try {
            // Fetch academic years if not already loaded
            console.log('Current academic years:', academicYears.length);
            if (academicYears.length === 0) {
                console.log('Fetching academic years...');
                await fetchAcademicYears();
            }

            // Get the latest academic years from store after fetch
            const currentAcademicYears = useAcademicYearStore.getState().academicYears;
            const currentSelectedYear = useAcademicYearStore.getState().selectedAcademicYear;
            console.log('Academic years after fetch:', currentAcademicYears.length, 'Selected:', currentSelectedYear);

            // Load students for the parent
            let selectedStudentData = null;
            if (user?.userId) {
                try {
                    console.log('Fetching students for parent:', user.userId);
                    const response = await getStudentsByParentId(user.userId);
                    console.log('Students response:', response);
                    if (response && response.data) {
                        const studentList = Array.isArray(response.data) ? response.data : [];
                        console.log('Students loaded:', studentList.length);
                        setStudents(studentList);
                        if (studentList.length > 0) {
                            selectedStudentData = studentList[0];
                            setSelectedStudent(selectedStudentData);
                            console.log('Selected student:', selectedStudentData);
                        }
                    }
                } catch (studentError) {
                    console.error('Error loading students:', studentError);
                    // If API endpoint not implemented, set empty array
                    setStudents([]);
                }
            }

            // Set default academic year if none selected
            let selectedYearData = currentSelectedYear;
            if (currentAcademicYears.length > 0 && !currentSelectedYear) {
                selectedYearData = currentAcademicYears[0];
                setSelectedAcademicYear(selectedYearData);
                console.log('Set default academic year:', selectedYearData);
            }

            // If we have both student and academic year, load fees and attendance immediately
            if (selectedStudentData && selectedYearData) {
                console.log('Loading fees and attendance...');
                await loadFeesAndAttendanceSync(selectedStudentData, selectedYearData);
                console.log('Fees and attendance loaded');
            } else {
                console.log('Skipping fees/attendance - student:', !!selectedStudentData, 'year:', !!selectedYearData);
            }
        } catch (error) {
            console.error('Error loading parent data:', error);
            // Don't show error if API endpoints are not yet implemented
            // uiHelper.showErrorMessage('Error loading student data');
        } finally {
            console.log('loadParentData finished, setting isLoading to false');
            setIsLoading(false);
        }
    };

    const loadFeesAndAttendanceSync = async (student: any, academicYear: any, showLoading: boolean = false) => {
        if (!student || !academicYear) return;

        if (showLoading) {
            setIsFetchingData(true);
        }

        try {
            // Load fees data for the selected student
            try {
                const feesResponse = await getParentFeesData({
                    studentId: student.studentId,
                    academicYearId: academicYear.academicYearId
                });

                console.log('Fees Response:', feesResponse); // Debug log
                if (feesResponse && feesResponse.data) {
                    setFeesData(Array.isArray(feesResponse.data) ? feesResponse.data : []);
                } else {
                    setFeesData([]);
                }
            } catch (error) {
                console.error('Error loading fees data:', error);
                setFeesData([]);
            }

            // Load attendance data for the selected student
            try {
                const attendanceResponse = await getParentAttendanceData({
                    parentId: user?.userId,
                    studentId: student.studentId,
                    academicYearId: academicYear.academicYearId
                });

                console.log('Attendance Response:', attendanceResponse); // Debug log
                if (attendanceResponse && attendanceResponse.data) {
                    setAttendanceData(Array.isArray(attendanceResponse.data) ? attendanceResponse.data : []);
                } else {
                    setAttendanceData([]);
                }
            } catch (error) {
                console.error('Error loading attendance data:', error);
                setAttendanceData([]);
            }

            // Load homework data for the selected student
            try {
                const homeworkResponse = await getHomeworkList({
                    params: {
                        studentId: student.studentId,
                        classId: student.classId
                    }
                });

                console.log('Homework Response:', homeworkResponse); // Debug log
                if (homeworkResponse && homeworkResponse.data) {
                    setHomeworkData(Array.isArray(homeworkResponse.data) ? homeworkResponse.data : []);
                } else {
                    setHomeworkData([]);
                }
            } catch (error) {
                console.error('Error loading homework data:', error);
                setHomeworkData([]);
            }
        } finally {
            if (showLoading) {
                setIsFetchingData(false);
            }
        }
    };

    const loadFeesAndAttendance = async () => {
        if (!selectedStudent || !selectedAcademicYear) return;
        await loadFeesAndAttendanceSync(selectedStudent, selectedAcademicYear, true);
    };

    const handleStudentChange = (option: any) => {
        if (option && option.student) {
            setSelectedStudent(option.student);
        } else {
            setSelectedStudent(null);
        }
    };

    const handleAcademicYearChange = (option: any) => {
        if (option && option.value) {
            const year = academicYears.find((y: any) => y.academicYearId === option.value);
            setSelectedAcademicYear(year || null);
        } else {
            setSelectedAcademicYear(null);
        }
    };

    // Calculate fees summary
    const feesSummary = useMemo(() => {
        if (!feesData.length) return { total: 0, paid: 0, pending: 0 };

        const total = feesData.reduce((sum, fee) => sum + (fee.totalFees || 0), 0);
        const paid = feesData.reduce((sum, fee) => sum + (fee.feesReceived || 0), 0);
        const pending = total - paid;

        return { total, paid, pending };
    }, [feesData]);

    // Calculate attendance summary
    const attendanceSummary = useMemo(() => {
        if (!attendanceData.length) return { totalDays: 0, absentDays: 0, presentDays: 0 };

        let totalDays = 0;
        let absentDays = 0;
        let presentDays = 0;

        attendanceData.forEach((record: any) => {
            // Handle flat array structure where each record is a daily attendance
            totalDays++;
            if (record.attendanceType === 1 || record.attendanceType === 4) presentDays++;
            if (record.attendanceType === 2) absentDays++;
        });

        return { totalDays, absentDays, presentDays };
    }, [attendanceData]);

    // Get absent days list
    const absentDaysList = useMemo(() => {
        const absentDays: any[] = [];
        attendanceData.forEach((record: any) => {
            // Handle flat array structure where each record is a daily attendance
            if (record.attendanceType === 2) { // Absent
                absentDays.push({
                    date: record.attendanceDay,
                    reason: record.notes || 'No reason provided'
                });
            }
        });
        return absentDays.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [attendanceData]);

    if (isLoading) {
        return <Loader />;
    }

    const handleViewFile = (attachmentUrl: string) => {
        if (attachmentUrl) {
          window.open(baseDocumentUrl + "/homework/" + clientId + "/" + attachmentUrl, '_blank');
        }
      };

    return (
        <div className="container-fluid group-data-[content=boxed]:max-w-boxed mx-auto px-3 md:px-0">
            <div className="flex flex-col gap-3 md:gap-4 py-3 md:py-6">
                {/* Header Card */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 rounded-xl shadow-lg overflow-hidden">
                    <div className="p-6 md:p-8">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                                    Parent Dashboard
                                </h1>
                                <p className="text-blue-100 dark:text-purple-100 text-sm md:text-base">
                                    Welcome back, {user?.name}
                                </p>
                            </div>
                            <div className="hidden md:flex items-center justify-center w-16 h-16 bg-white/20 rounded-full">
                                <Users className="w-8 h-8 text-white" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Selection and Navigation Card */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
                    <div className="p-4 md:p-6">
                        {/* Student and Academic Year Selection */}
                        <div className="grid grid-cols-1 gap-4 md:gap-6 md:grid-cols-2 mb-6">

                        <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                    Academic Year
                                </label>
                                <Select
                                    options={academicYearOptions}
                                    value={selectedAcademicYear ? academicYearOptions.find(opt => opt.value === selectedAcademicYear.academicYearId) : null}
                                    onChange={handleAcademicYearChange}
                                    placeholder="Choose academic year..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isDisabled={isFetchingData}
                                    isLoading={isFetchingData}
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                    Select Student
                                </label>
                                <Select
                                    options={studentOptions}
                                    value={selectedStudent ? studentOptions.find(opt => opt.value === selectedStudent.studentId) : null}
                                    onChange={handleStudentChange}
                                    placeholder="Choose a student..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isDisabled={isFetchingData}
                                    isLoading={isFetchingData}
                                />
                            </div>
                         
                        </div>

                        {/* Divider */}
                        <div className="border-t border-gray-200 dark:border-gray-700 mb-6"></div>

                        {/* Navigation Tabs */}
                        <div>
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                                Navigation
                            </p>
                            <div className="bg-gray-100 dark:bg-gray-900 rounded-lg p-1 flex gap-1">
                                <button
                                    onClick={() => setActiveTab('overview')}
                                    className={`flex-1 px-4 py-3 rounded-md text-sm font-semibold transition-all duration-200 ${
                                        activeTab === 'overview'
                                            ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-md transform scale-105'
                                            : 'text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800'
                                    }`}
                                >
                                    <span className="flex items-center justify-center gap-2">
                                        <Calendar className="w-4 h-4" />
                                        <span className="hidden sm:inline">Overview</span>
                                    </span>
                                </button>
                                <button
                                    onClick={() => setActiveTab('fees')}
                                    className={`flex-1 px-4 py-3 rounded-md text-sm font-semibold transition-all duration-200 ${
                                        activeTab === 'fees'
                                            ? 'bg-gradient-to-r from-green-600 to-green-500 text-white shadow-md transform scale-105'
                                            : 'text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800'
                                    }`}
                                >
                                    <span className="flex items-center justify-center gap-2">
                                        <DollarSign className="w-4 h-4" />
                                        <span className="hidden sm:inline">Fees</span>
                                    </span>
                                </button>
                                <button
                                    onClick={() => setActiveTab('attendance')}
                                    className={`flex-1 px-4 py-3 rounded-md text-sm font-semibold transition-all duration-200 ${
                                        activeTab === 'attendance'
                                            ? 'bg-gradient-to-r from-purple-600 to-purple-500 text-white shadow-md transform scale-105'
                                            : 'text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800'
                                    }`}
                                >
                                    <span className="flex items-center justify-center gap-2">
                                        <UserCheck className="w-4 h-4" />
                                        <span className="hidden sm:inline">Attendance</span>
                                    </span>
                                </button>
                                <button
                                    onClick={() => setActiveTab('homework')}
                                    className={`flex-1 px-4 py-3 rounded-md text-sm font-semibold transition-all duration-200 ${
                                        activeTab === 'homework'
                                            ? 'bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-md transform scale-105'
                                            : 'text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800'
                                    }`}
                                >
                                    <span className="flex items-center justify-center gap-2">
                                        <BookOpen className="w-4 h-4" />
                                        <span className="hidden sm:inline">Homework</span>
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content based on active tab */}
                {selectedStudent && selectedAcademicYear ? (
                    <div className="mt-3 md:mt-6 relative">
                        {/* Loading Overlay */}
                        {isFetchingData && (
                            <div className="absolute inset-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl z-10 flex items-center justify-center">
                                <div className="flex flex-col items-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                                    <p className="mt-4 text-sm font-medium text-gray-700 dark:text-gray-300">Loading data...</p>
                                </div>
                            </div>
                        )}
                        {activeTab === 'overview' && (
                            <div className="grid grid-cols-1 gap-3 md:gap-6 sm:grid-cols-2 lg:grid-cols-4">
                                {/* Student Info Card */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6">
                                    <div className="flex items-center">
                                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                            <Users className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
                                        </div>
                                        <div className="ml-3 md:ml-4">
                                            <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">Student</p>
                                            <p className="text-sm md:text-lg font-semibold text-gray-900 dark:text-white">
                                                {selectedStudent.firstName} {selectedStudent.lastName}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Fees Summary */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6">
                                    <div className="flex items-center">
                                        <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                            <DollarSign className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
                                        </div>
                                        <div className="ml-3 md:ml-4">
                                            <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">Total Fees</p>
                                            <p className="text-sm md:text-lg font-semibold text-gray-900 dark:text-white">
                                                ${feesSummary.total.toFixed(2)}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Paid Amount */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6">
                                    <div className="flex items-center">
                                        <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                            <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
                                        </div>
                                        <div className="ml-3 md:ml-4">
                                            <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">Paid</p>
                                            <p className="text-sm md:text-lg font-semibold text-gray-900 dark:text-white">
                                                ${feesSummary.paid.toFixed(2)}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Pending Amount */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6">
                                    <div className="flex items-center">
                                        <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                                            <AlertCircle className="w-5 h-5 md:w-6 md:h-6 text-red-600" />
                                        </div>
                                        <div className="ml-3 md:ml-4">
                                            <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">Pending</p>
                                            <p className="text-sm md:text-lg font-semibold text-gray-900 dark:text-white">
                                                ${feesSummary.pending.toFixed(2)}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Attendance Summary */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6 sm:col-span-2 lg:col-span-4">
                                    <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white mb-4">Attendance Summary</h3>
                                    <div className="grid grid-cols-3 gap-3 md:gap-6">
                                        <div className="text-center">
                                            <div className="flex items-center justify-center mb-2">
                                                <UserCheck className="w-6 h-6 md:w-8 md:h-8 text-green-600" />
                                            </div>
                                            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Present Days</p>
                                            <p className="text-lg md:text-2xl font-bold text-green-600">{attendanceSummary.presentDays}</p>
                                        </div>
                                        <div className="text-center">
                                            <div className="flex items-center justify-center mb-2">
                                                <UserX className="w-6 h-6 md:w-8 md:h-8 text-red-600" />
                                            </div>
                                            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Absent Days</p>
                                            <p className="text-lg md:text-2xl font-bold text-red-600">{attendanceSummary.absentDays}</p>
                                        </div>
                                        <div className="text-center">
                                            <div className="flex items-center justify-center mb-2">
                                                <Calendar className="w-6 h-6 md:w-8 md:h-8 text-blue-600" />
                                            </div>
                                            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Total Days</p>
                                            <p className="text-lg md:text-2xl font-bold text-blue-600">{attendanceSummary.totalDays}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'fees' && (
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                                <div className="p-4 md:p-6 border-b border-gray-200 dark:border-gray-700">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Fees & Payments
                                    </h3>
                                    <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">
                                        {selectedAcademicYear.academicYearName} • {feesData.length} Record{feesData.length !== 1 ? 's' : ''}
                                    </p>
                                </div>
                                <div className="p-4 md:p-6">
                                    {feesData.length > 0 ? (
                                        <>
                                            {/* Mobile Card View */}
                                            <div className="block md:hidden space-y-4">
                                                {feesData.map((fee: any, index: number) => {
                                                    const netAmount = (fee.totalFees || 0) - (fee.discount || 0);
                                                    const paidAmount = fee.feesReceived || 0;
                                                    const isPaid = paidAmount >= netAmount;
                                                    const isPartial = paidAmount > 0 && paidAmount < netAmount;
                                                    
                                                    // Parse the feeMonthYear date
                                                    const feeDate = fee.feeMonthYear ? new Date(fee.feeMonthYear) : null;
                                                    const monthYear = feeDate 
                                                        ? `${feeDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`
                                                        : 'N/A';
                                                    
                                                    return (
                                                        <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center space-x-2">
                                                                    <Calendar className="w-5 h-5 text-blue-600" />
                                                                    <span className="font-semibold text-gray-900 dark:text-white">
                                                                        {monthYear}
                                                                    </span>
                                                                </div>
                                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                                    isPaid 
                                                                        ? 'bg-green-100 text-green-800' 
                                                                        : isPartial 
                                                                        ? 'bg-yellow-100 text-yellow-800'
                                                                        : 'bg-red-100 text-red-800'
                                                                }`}>
                                                                    {isPaid ? 'Paid' : isPartial ? 'Partial' : 'Pending'}
                                                                </span>
                                                            </div>
                                                            <div className="grid grid-cols-2 gap-3">
                                                                <div>
                                                                    <p className="text-xs text-gray-500 dark:text-gray-400">Total Fees</p>
                                                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                                        ${fee?.totalFees?.toFixed(2) || '0.00'}
                                                                    </p>
                                                                </div>
                                                                <div>
                                                                    <p className="text-xs text-gray-500 dark:text-gray-400">Discount</p>
                                                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                                        ${fee?.discount?.toFixed(2) || '0.00'}
                                                                    </p>
                                                                </div>
                                                                <div>
                                                                    <p className="text-xs text-gray-500 dark:text-gray-400">Amount Paid</p>
                                                                    <p className="text-sm font-medium text-green-600">
                                                                        ${paidAmount.toFixed(2)}
                                                                    </p>
                                                                </div>
                                                                <div>
                                                                    <p className="text-xs text-gray-500 dark:text-gray-400">Balance</p>
                                                                    <p className="text-sm font-medium text-red-600">
                                                                        ${(netAmount - paidAmount).toFixed(2)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {/* Desktop Table View */}
                                            <div className="hidden md:block overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                                    <thead className="bg-gray-50 dark:bg-gray-700">
                                                        <tr>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                                Month
                                                            </th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                                Total Fees
                                                            </th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                                Discount
                                                            </th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                                Amount Paid
                                                            </th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                                Balance
                                                            </th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                                Status
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                                        {feesData.map((fee: any, index: number) => {
                                                            const netAmount = (fee.totalFees || 0) - (fee.discount || 0);
                                                            const paidAmount = fee.feesReceived || 0;
                                                            const isPaid = paidAmount >= netAmount;
                                                            const isPartial = paidAmount > 0 && paidAmount < netAmount;
                                                            
                                                            // Parse the feeMonthYear date
                                                            const feeDate = fee.feeMonthYear ? new Date(fee.feeMonthYear) : null;
                                                            const monthYear = feeDate 
                                                                ? `${feeDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`
                                                                : 'N/A';
                                                            
                                                            return (
                                                                <tr key={index}>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                                                        {monthYear}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                                                                        ${fee?.totalFees?.toFixed(2) || '0.00'}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                                                                        ${fee?.discount?.toFixed(2) || '0.00'}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 dark:text-green-400 font-medium">
                                                                        ${paidAmount.toFixed(2)}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400 font-medium">
                                                                        ${(netAmount - paidAmount).toFixed(2)}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                                            isPaid 
                                                                                ? 'bg-green-100 text-green-800' 
                                                                                : isPartial 
                                                                                ? 'bg-yellow-100 text-yellow-800'
                                                                                : 'bg-red-100 text-red-800'
                                                                        }`}>
                                                                            {isPaid ? 'Paid' : isPartial ? 'Partial' : 'Pending'}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-center py-8">
                                            <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
                                            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No fees data</h3>
                                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                                No fees information available for the selected academic year.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'attendance' && (
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                                <div className="p-4 md:p-6 border-b border-gray-200 dark:border-gray-700">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Attendance
                                    </h3>
                                    <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">
                                        {selectedAcademicYear.academicYearName}
                                    </p>
                                </div>
                                <div className="p-4 md:p-6">
                                    {absentDaysList.length > 0 ? (
                                        <div>
                                            <h4 className="text-sm md:text-md font-medium text-gray-900 dark:text-white mb-4">
                                                Absent Days ({absentDaysList.length})
                                            </h4>
                                            <div className="space-y-3">
                                                {absentDaysList.map((day: any, index: number) => (
                                                    <div key={index} className="flex items-start p-3 md:p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                                                        <div className="flex-shrink-0">
                                                            <Calendar className="w-5 h-5 text-red-600" />
                                                        </div>
                                                        <div className="ml-3 flex-1">
                                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                                {new Date(day.date).toLocaleDateString('en-US', {
                                                                    weekday: 'long',
                                                                    year: 'numeric',
                                                                    month: 'long',
                                                                    day: 'numeric'
                                                                })}
                                                            </p>
                                                            {day.reason && day.reason !== 'No reason provided' && (
                                                                <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                                    Reason: {day.reason}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-8">
                                            <UserCheck className="mx-auto h-12 w-12 text-green-400" />
                                            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Perfect Attendance</h3>
                                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                                No absent days found for the selected academic year.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'homework' && (
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                                <div className="p-4 md:p-6 border-b border-gray-200 dark:border-gray-700">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Homework Assignments
                                    </h3>
                                    <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">
                                        {selectedAcademicYear.academicYearName} • {homeworkData.length} Assignment{homeworkData.length !== 1 ? 's' : ''}
                                    </p>
                                </div>
                                <div className="p-4 md:p-6">
                                    {homeworkData.length > 0 ? (
                                        <div className="space-y-4">
                                            {homeworkData.map((homework: any, index: number) => {
                                                const assignedDate = homework.assignedDate ? new Date(homework.assignedDate) : null;
                                                const dueDate = homework.dueDate ? new Date(homework.dueDate) : null;
                                                const today = new Date();
                                                const isOverdue = dueDate && dueDate < today;
                                                const isDueSoon = dueDate && !isOverdue && (dueDate.getTime() - today.getTime()) < (3 * 24 * 60 * 60 * 1000); // Due within 3 days

                                                return (
                                                    <div 
                                                        key={index} 
                                                        className={`border rounded-lg p-4 md:p-5 ${
                                                            isOverdue 
                                                                ? 'border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700' 
                                                                : isDueSoon 
                                                                ? 'border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-700'
                                                                : 'border-gray-200 dark:border-gray-700'
                                                        }`}
                                                    >
                                                        <div className="flex items-start justify-between mb-3">
                                                            <div className="flex items-start flex-1">
                                                                <div className={`flex-shrink-0 p-2 rounded-lg ${
                                                                    isOverdue 
                                                                        ? 'bg-red-100 dark:bg-red-900/30' 
                                                                        : isDueSoon 
                                                                        ? 'bg-yellow-100 dark:bg-yellow-900/30'
                                                                        : 'bg-orange-100 dark:bg-orange-900/30'
                                                                }`}>
                                                                    <BookOpen className={`w-5 h-5 ${
                                                                        isOverdue 
                                                                            ? 'text-red-600' 
                                                                            : isDueSoon 
                                                                            ? 'text-yellow-600'
                                                                            : 'text-orange-600'
                                                                    }`} />
                                                                </div>
                                                                <div className="ml-3 flex-1">
                                                                    <h4 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">
                                                                        {homework.title}
                                                                    </h4>
                                                                   
                                                                </div>
                                                            </div>
                                                            {isOverdue && (
                                                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 ml-2">
                                                                    Overdue
                                                                </span>
                                                            )}
                                                            {isDueSoon && !isOverdue && (
                                                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 ml-2">
                                                                    Due Soon
                                                                </span>
                                                            )}
                                                        </div>
                                                        
                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                                                            <div>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400">Assigned Date</p>
                                                                <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                                                                    {assignedDate 
                                                                        ? assignedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                                                        : 'N/A'}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400">Due Date</p>
                                                                <p className={`text-sm font-medium mt-1 ${
                                                                    isOverdue 
                                                                        ? 'text-red-600' 
                                                                        : isDueSoon 
                                                                        ? 'text-yellow-600'
                                                                        : 'text-gray-900 dark:text-white'
                                                                }`}>
                                                                    {dueDate 
                                                                        ? dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                                                        : 'N/A'}
                                                                </p>
                                                            </div>
                                                            {/* <div>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400">Total Marks</p>
                                                                <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                                                                    {homework.totalMarks || 0}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400">Teacher</p>
                                                                <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                                                                    {homework.teacherName || 'N/A'}
                                                                </p>
                                                            </div> */}
                                                        </div>
                                                        
                                                        {homework.description && (
                                                            <div className="mt-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                                                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                                                    {homework.description}
                                                                </p>
                                                            </div>
                                                        )}

                                                        {homework.attachmentUrl && (
                                                            <div className="mt-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                                                                <a 
                                                                    href={baseDocumentUrl + "/homework/" + clientId + "/" + homework.attachmentUrl} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                                                >
                                                                    <AlertCircle className="w-4 h-4 mr-1" />
                                                                    View Attachment
                                                                </a>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8">
                                            <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
                                            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No Homework</h3>
                                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                                No homework assignments found for the selected student.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-8 md:py-12 px-4">
                        <Users className="mx-auto h-10 w-10 md:h-12 md:w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm md:text-base font-medium text-gray-900 dark:text-white">Select Student and Academic Year</h3>
                        <p className="mt-1 text-xs md:text-sm text-gray-500 dark:text-gray-400">
                            Please select a student and academic year to view their information.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ParentDashboard;
